import { DDSTooltip, DDSTypography } from 'den-design-system'
import React, { useEffect, useState } from 'react'
import { UrlForSalesforceConnect, replaceUrl } from '../../utils/UrlUtils'
import { groupBy, isEmpty, isNull } from 'lodash'

import { Button } from '../../components/New/Button/Button'
import PreferenceDropDown from '../../components/SalesforceAudit/PreferenceDropDown'
import SFFileStorageCard from '../../components/SalesforceAudit/SFFileStoragecard'
import SFLicenseDistributionCard from '../../components/SalesforceAudit/SFLicenseDistributionCard'
import SFMetadataCard from '../../components/SalesforceAudit/SFMetaDataCard'
import SFUsageCard from '../../components/SalesforceAudit/SFUsageCard'
import SFUserStatsCard from '../../components/SalesforceAudit/SFUserStatsCard'
import { SalesforceLimitType } from '../../enum/SalesforceLimitType.enum'
import Spinner from '../../components/common/Spinner/Spinner'
import Timezone from '../../components/common/Timezone/Timezone'
import TimezoneConstants from '../../constants/TimezoneConstants'
import { ToastMessageConstants } from '../../constants/ToastMessageConstants'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import arrowRightIcon from '../../assets/icons/arrowRight.svg'
import { formatTimestampByTimezone } from '../../utils/DateTimeUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import routeConstants from '../../constants/RouteConstants'
import { updateSalesforceAuditDetails } from '../../api/salesforceAudit/salesforceAudit.service'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

interface SectionTitleProps {
  title: string
  link?: string
  alignSelf?: 'auto' | 'start' | 'end' | 'center' | 'stretch'
}

const SectionTitle: React.FC<SectionTitleProps> = ({
  title,
  link,
  alignSelf = 'auto'
}) => {
  const navigate = useNavigate()
  const { scopeStore } = useStoreContext()
  const handleNavigation = (link: string) => {
    navigate(
      replaceUrl(
        link,
        scopeStore.getScope()['organization-id'] as string,
        scopeStore.getScope()['project-id'] as string,
        scopeStore.getScope()['environment-id'] as string
      )
    )
  }
  return (
    <div className={`flex flex-col self-${alignSelf}`}>
      <div
        className={`flex flex-row items-center ${link ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => link && handleNavigation(link)}
      >
        <div>
          <span
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: pallete.colors.textDark3
            }}
          >
            {title}
          </span>
        </div>
        {link && (
          <img
            src={arrowRightIcon}
            alt='arrow'
            className='h-[16px] w-[16px] ml-[8px] translate-y-[1px]'
          />
        )}
      </div>
    </div>
  )
}

const SalesforceAuditDashboard: React.FC = () => {
  const { salesforceAuditStore } = useStoreContext()
  const [preferenceTime, setPreferenceTime] = useState<string>('6')
  const auditData = salesforceAuditStore.getAuditData()
  const limits = salesforceAuditStore.getLimitsData()
  const loading = salesforceAuditStore.getLoading()
  const groupedLimits: any = groupBy(limits, 'name')

  const totalStandardLicenses = salesforceAuditStore.getTotalLicenses()
  const usedStandardLicenses = salesforceAuditStore.getUsedLicenses()
  const mostCommonStandard = salesforceAuditStore.getMostCommonLicense()

  const totalPermissionLicenses =
    salesforceAuditStore.getTotalPermissionSetsLicenses()
  const usedPermissionLicenses =
    salesforceAuditStore.getUsedPermissionSetsLicenses()
  const mostCommonPermission =
    salesforceAuditStore.getMostCommonPermissionSetsLicenses()

  const totalLicenses = totalStandardLicenses + totalPermissionLicenses
  const usedLicenses = usedStandardLicenses + usedPermissionLicenses

  const rawUsage = (usedLicenses / totalLicenses) * 100
  const usage =
    rawUsage > 0 && rawUsage < 0.01 ? 0.01 : Number(rawUsage.toFixed(2))

  const totalLicenseDistribution =
    salesforceAuditStore.getTotalLicenseDistribution()

  const mostCommonLicense =
    (usedStandardLicenses >= usedPermissionLicenses
      ? mostCommonStandard
      : mostCommonPermission) || {}

  const store = useStoreContext()
  const organizationPreference =
    store.userStore.getOrganizationPreference()?.timeZone
  const matchedTimeZone = TimezoneConstants.find(
    (tz) => tz?.tzCode === organizationPreference
  )

  const handleReconnect = () => {
    try {
      window.location.href = UrlForSalesforceConnect(
        store.scopeStore.getScope()['organization-id'] as string,
        store.scopeStore.getScope()['project-id'] as string,
        store.scopeStore.getScope()['environment-id'] as string,
        'existing',
        true
      )
    } catch (error) {
      ToastNotification({
        type: 'error',
        message:
          ToastMessageConstants.SALESFORCE_AUDIT.CONNECT_TO_SALESFORCE.ERROR
      })
    }
  }

  const handleAuditFrequencyChange = async (value: any) => {
    try {
      setPreferenceTime(value)
      const auditData = salesforceAuditStore.getAuditData()

      await updateSalesforceAuditDetails({
        $id: auditData?.salesforceAuditId,
        auditFrequencyHours: Number(value)
      })
      ToastNotification({
        type: 'success',
        message:
          ToastMessageConstants.SALESFORCE_AUDIT.UPDATE_AUDIT_FREQUENCY.SUCCESS
      })
    } catch (error) {
      ToastNotification({
        type: 'error',
        message:
          ToastMessageConstants.SALESFORCE_AUDIT.UPDATE_AUDIT_FREQUENCY.ERROR
      })
    }
  }

  const preference = [
    {
      label: '2 hours',
      value: '2'
    },
    {
      label: '4 hours',
      value: '4'
    },
    {
      label: '6 hours',
      value: '6'
    },
    {
      label: '8 hours',
      value: '8'
    }
  ]

  useEffect(() => {
    store.breadcrumbStore.setBreadcrumbsOptions({
      label: 'Salesforce Audit',
      link: replaceUrl(
        routeConstants.SALESFORCE_AUDIT_DASHBOARD,
        store.scopeStore.getScope()['organization-id'] as string,
        store.scopeStore.getScope()['project-id'] as string,
        store.scopeStore.getScope()['environment-id'] as string
      )
    })
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  useEffect(() => {
    if (auditData && auditData.auditFrequencyHours) {
      // Convert the auditFrequencyHours to string and set it
      setPreferenceTime(auditData.auditFrequencyHours.toString())
    }
  }, [auditData])

  const used = +salesforceAuditStore.getUsedFileStorage().toFixed(2)
  const max = groupedLimits['FileStorageMB'][0].maximum

  const percent = Math.min(+((used / max) * 100).toFixed(2), 100)

  return loading || isEmpty(auditData) ? (
    <Spinner />
  ) : (
    <div className='flex flex-col w-full pb-[24px] gap-[24px]'>
      <div className='flex w-full justify-between items-start'>
        {/* Left Side */}
        <div className='flex flex-col gap-[12px]'>
          <div>
            <DDSTypography.Title
              type='h4'
              variant='semiBold'
              color={pallete.colors.black}
            >
              Salesforce Instance URL
            </DDSTypography.Title>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark6}
            >
              {auditData.instanceUrl}
            </DDSTypography.Paragraph>
          </div>
          <div className='flex items-center gap-[12px] pt-[12px]'>
            <span
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: pallete.colors.black
              }}
            >
              Audit Frequency
            </span>
            <div
              className='flex items-center rounded-[6px] h-[32px]'
              style={{
                border: `1px solid ${pallete.colors.neuButtonStokeDis}`
              }}
            >
              <PreferenceDropDown
                options={preference}
                value={preferenceTime}
                onChange={(value: any) => {
                  handleAuditFrequencyChange(value)
                }}
                placeholder='6 hours'
              />
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className='flex flex-col items-end gap-2'>
          <Timezone
            tooltipText='Time zone is based on organization settings'
            text={
              <div>
                {matchedTimeZone?.tzCode} {matchedTimeZone?.label}
              </div>
            }
            textColor={pallete.colors.neuButtonText}
          />
          <div
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 400,
              color: pallete.colors.textDark5
            }}
          >
            {(() => {
              if (!auditData.lastSyncDate) {
                return 'Sync data unavailable'
              }
              if (matchedTimeZone?.tzCode) {
                return `Last sync on ${formatTimestampByTimezone(
                  auditData.lastSyncDate,
                  matchedTimeZone.tzCode
                )}`
              }
              return `Last sync on ${auditData.lastSyncDate}`
            })()}
          </div>
          <div className='pt-[8px]'>
            <DDSTooltip
              id='tooltip-reconnect'
              label={
                <DDSTypography.Paragraph
                  size='para'
                  variant='regular'
                  color={pallete.colors.textDark4}
                >
                  Reconnects to your connected Salesforce instance. Changing the
                  instance is not supported.
                </DDSTypography.Paragraph>
              }
              shape='roundedRectangle'
              position='top'
              theme='light'
              className='border override-tooltip-arrow max-w-[24%]'
              style={{
                borderColor: pallete.colors.stroke2,
                zIndex: 99999
              }}
            >
              <Button
                id='sf-reconnect'
                type='filled'
                size='small'
                label='Reconnect'
                onClick={handleReconnect}
              />
            </DDSTooltip>
          </div>
        </div>
      </div>

      {/* Row 2: API Info */}
      <div className='flex flex-col gap-[8px]'>
        <DDSTypography.Title
          type='h4'
          variant='semiBold'
          color={pallete.colors.textDark3}
        >
          {`API's`}
        </DDSTypography.Title>
        <DDSTypography.Paragraph
          size='para'
          variant='medium'
          color={pallete.colors.textDark6}
          className='pb-[12px]'
        >
          API usage resets every 24hrs
        </DDSTypography.Paragraph>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-[14px]'>
          <SFUsageCard
            title={'API'}
            usage={
              +(
                (1 -
                  groupedLimits['DailyApiRequests'][0].remaining /
                    groupedLimits['DailyApiRequests'][0].maximum) *
                100
              ).toFixed(2)
            }
            requests={{
              title: 'Total Requests',
              value: !isNull(groupedLimits['DailyApiRequests'][0].maximum)
                ? groupedLimits['DailyApiRequests'][0].maximum -
                  groupedLimits['DailyApiRequests'][0].remaining
                : -1
            }}
            limit={{
              title: 'Daily Limit',
              value: !isNull(groupedLimits['DailyApiRequests'][0].maximum)
                ? groupedLimits['DailyApiRequests'][0].maximum
                : -1
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_USAGE_DATA_OVERVIEW +
              '/' +
              SalesforceLimitType.DAILY_API_REQUESTS
            }
          />
          <SFUsageCard
            title={'Streaming API Events'}
            usage={
              +(
                (1 -
                  groupedLimits['DailyStreamingApiEvents'][0].remaining /
                    groupedLimits['DailyStreamingApiEvents'][0].maximum) *
                100
              ).toFixed(2)
            }
            requests={{
              title: 'Event Request',
              value: !isNull(
                groupedLimits['DailyStreamingApiEvents'][0].maximum
              )
                ? groupedLimits['DailyStreamingApiEvents'][0].maximum -
                  groupedLimits['DailyStreamingApiEvents'][0].remaining
                : -1
            }}
            limit={{
              title: 'Daily Limit',
              value: !isNull(
                groupedLimits['DailyStreamingApiEvents'][0].maximum
              )
                ? groupedLimits['DailyStreamingApiEvents'][0].maximum
                : -1
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_USAGE_DATA_OVERVIEW +
              '/' +
              SalesforceLimitType.DAILY_STREAMING_API_EVENTS
            }
          />

          <SFUsageCard
            title={'Bulk API batches'}
            usage={
              +(
                (1 -
                  groupedLimits['DailyBulkApiBatches'][0].remaining /
                    groupedLimits['DailyBulkApiBatches'][0].maximum) *
                100
              ).toFixed(2)
            }
            requests={{
              title: 'Batches Requested',
              value: !isNull(groupedLimits['DailyBulkApiBatches'][0].maximum)
                ? groupedLimits['DailyBulkApiBatches'][0].maximum -
                  groupedLimits['DailyBulkApiBatches'][0].remaining
                : -1
            }}
            limit={{
              title: 'Daily Limit',
              value: !isNull(groupedLimits['DailyBulkApiBatches'][0].maximum)
                ? groupedLimits['DailyBulkApiBatches'][0].maximum
                : -1
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_USAGE_DATA_OVERVIEW +
              '/' +
              SalesforceLimitType.DAILY_BULK_API_BATCHES
            }
          />

          <SFUsageCard
            title={'Functions API Calls'}
            usage={
              +(
                (1 -
                  groupedLimits['DailyFunctionsApiCallLimit'][0].remaining /
                    groupedLimits['DailyFunctionsApiCallLimit'][0].maximum) *
                100
              ).toFixed(2)
            }
            requests={{
              title: 'Total Calls',
              value: !isNull(
                groupedLimits['DailyFunctionsApiCallLimit'][0].maximum
              )
                ? groupedLimits['DailyFunctionsApiCallLimit'][0].maximum -
                  groupedLimits['DailyFunctionsApiCallLimit'][0].remaining
                : -1
            }}
            limit={{
              title: 'Daily Limit',
              value: !isNull(
                groupedLimits['DailyFunctionsApiCallLimit'][0].maximum
              )
                ? groupedLimits['DailyFunctionsApiCallLimit'][0].maximum
                : -1
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_USAGE_DATA_OVERVIEW +
              '/' +
              SalesforceLimitType.DAILY_FUNCTIONS_API_CALL_LIMIT
            }
          />

          {groupedLimits['SingleEmail']?.[0] && (
            <SFUsageCard
              title={'Single Email'}
              usage={
                +(
                  (1 -
                    groupedLimits['SingleEmail'][0]?.remaining /
                      groupedLimits['SingleEmail'][0]?.maximum) *
                  100
                ).toFixed(2)
              }
              requests={{
                title: 'Emails Sent',
                value: !isNull(groupedLimits['SingleEmail'][0]?.maximum)
                  ? groupedLimits['SingleEmail'][0]?.maximum -
                    groupedLimits['SingleEmail'][0]?.remaining
                  : -1
              }}
              limit={{
                title: 'Daily Limit',
                value: !isNull(groupedLimits['SingleEmail'][0]?.maximum)
                  ? groupedLimits['SingleEmail'][0]?.maximum
                  : -1
              }}
              link={
                routeConstants.SALESFORCE_AUDIT_USAGE_DATA_OVERVIEW +
                '/' +
                SalesforceLimitType.SINGLE_EMAIL
              }
            />
          )}
        </div>
      </div>

      {/* Row 3: MetaData Info */}
      <div className='flex flex-col gap-[8px]'>
        <DDSTypography.Title
          type='h4'
          variant='semiBold'
          color={pallete.colors.textDark3}
        >
          Metadata
        </DDSTypography.Title>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-[14px]'>
          <SFMetadataCard
            title='Apex Classes'
            total={{
              title: 'Total Classes',
              value: auditData.apexClassesCount
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW +
              '/apex-classes'
            }
          />
          <SFMetadataCard
            title='Apex Triggers'
            total={{
              title: 'Total Triggers',
              value: auditData.apexTriggersCount
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW +
              '/apex-triggers'
            }
          />
          <SFMetadataCard
            title='Permission Sets'
            total={{
              title: 'Total Sets',
              value: auditData.permissionSetsCount
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW +
              '/permission-sets'
            }
          />
          <SFMetadataCard
            title='Profiles'
            total={{
              title: 'Total Profiles',
              value: auditData.profilesCount
            }}
            link={
              routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/profiles'
            }
          />
          <SFMetadataCard
            title='Roles'
            total={{
              title: 'Total Roles',
              value: auditData.rolesCount
            }}
            link={routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/roles'}
          />
        </div>
      </div>

      {/* Row 4: license Info */}
      <div className='flex flex-col gap-[8px]'>
        <SectionTitle
          title='Licenses'
          link={routeConstants.SALESFORCE_AUDIT_LICENSES_OVERVIEW}
        />
        <SFLicenseDistributionCard
          usage={usage}
          mostCommonLicense={
            mostCommonLicense?.Name || mostCommonLicense?.MasterLabel
          }
          totalLicenses={totalLicenses === 0 ? -1 : totalLicenses}
          usedLicenses={totalLicenses === 0 ? -1 : usedLicenses}
          distributions={totalLicenseDistribution}
        />
      </div>

      {/* Row 5: fileStorage Info */}

      <div className='flex flex-row gap-[16px] w-full items-stretch'>
        {/* Users Card (30%) */}
        <div className='flex flex-col gap-[8px] w-[20%] h-full'>
          <SectionTitle
            title='Users'
            link={routeConstants.SALESFORCE_AUDIT_USERS_OVERVIEW}
          />
          <div className='h-full'>
            <SFUserStatsCard
              title='Users'
              total={{
                title: 'Total Users',
                value: auditData.totalUserCount
              }}
              limit={{
                title: 'Active Users',
                value: auditData.activeUserCount
              }}
            />
          </div>
        </div>

        {/* File Storage Card (70%) */}
        <div className='flex flex-col gap-[8px] w-[80%] h-full'>
          <SectionTitle title='File Storage' />
          <div className='h-full'>
            <SFFileStorageCard
              usage={percent}
              totalStorageInMB={max}
              availableStorageInMB={Math.max(max - used, 0)}
              totalFiles={auditData.files.length}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default observer(SalesforceAuditDashboard)
